import Client from "..";
import { WheelSection, WheelSectionColors } from "../wheel/WheelSection";

export abstract class Effect {
  icon: string;
  name: string;
  description: string;
  duration: number; // -1 for infinite duration

  constructor(
    icon: string,
    name: string,
    description: string,
    duration: number,
  ) {
    this.icon = icon;
    this.name = name;
    this.description = description;
    this.duration = duration;
  }

  abstract Clone(): Effect;

  public OnUseEffect(_dropZoneParent?: unknown): boolean {
    // Should trigger only once
    return true;
  }

  public ApplyEffect(_dropZoneParent?: unknown): void {
    // Should trigger every spin / round
    if (this.duration !== -1) {
      this.duration--;
      if (this.duration <= 0) this.EffectExpires();
    }
  }

  public EffectExpires(_dropZoneParent?: unknown): void {
    // Trigger an effect expired event or callback if needed
  }
}

export class BankruptcyEffect extends Effect {
  constructor() {
    super("effect_bankruptcy.png", "Bankruptcy", "Lose all your money!", -1);
  }

  public Clone(): BankruptcyEffect {
    return new BankruptcyEffect();
  }

  public override ApplyEffect(): void {
    Client.state.score = 0;
    super.ApplyEffect();
  }
}

export class ExtraSpinEffect extends Effect {
  constructor() {
    super("effect_boostScore.png", "Extra Spin", "Gain an extra spin!", -1);
  }

  public Clone(): ExtraSpinEffect {
    return new ExtraSpinEffect();
  }

  public override ApplyEffect(): void {
    Client.state.availableSpins += 1;
    super.ApplyEffect();
  }
}

export class ExtraSpinOnUseEffect extends Effect {
  constructor() {
    super("effect_boostScore.png", "Extra Spin", "Gain an extra spin!", 0);
  }

  public Clone(): ExtraSpinOnUseEffect {
    return new ExtraSpinOnUseEffect();
  }

  public override OnUseEffect(): boolean {
    if (!super.OnUseEffect()) return false;
    Client.state.availableSpins += 1;
    return true;
  }
}

export class AddBrakeEffect extends Effect {
  constructor() {
    super("effect_brake.png", "Brake", "Slows down the wheel!", 0);
  }

  public Clone(): AddBrakeEffect {
    return new AddBrakeEffect();
  }

  public override OnUseEffect(): boolean {
    if (!super.OnUseEffect()) return false;
    return Client.getActiveScene()?.addBrake() ?? false;
  }
}

export class MultiplySectionScoreEffect extends Effect {
  constructor(readonly multiplier: number) {
    super(
      "effect_boostScore.png",
      "Multiply Score",
      `Permanently multiply the current score of a section by x${multiplier}.`,
      0,
    );
  }

  public Clone(): MultiplySectionScoreEffect {
    return new MultiplySectionScoreEffect(this.multiplier);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      dropZoneParent.score *= this.multiplier;
      return true;
    }

    return false;
  }
}

export class ModifySectionScoreEffect extends Effect {
  constructor(
    readonly amount: number,
    duration: number = 0, // Default to single use (OnUseEffect behavior)
  ) {
    const isMultiUse = duration > 0;
    super(
      "effect_boostScore.png",
      `${amount < 0 ? "Remove" : "Add"} Section Score`,
      isMultiUse
        ? `${amount < 0 ? "Remove" : "Add"} ${Math.abs(amount)} points to the last scored section for ${duration} rounds!`
        : `Permanently ${amount < 0 ? "remove" : "add"} ${Math.abs(amount)} points to a section.`,
      duration,
    );
  }

  public Clone(): ModifySectionScoreEffect {
    return new ModifySectionScoreEffect(this.amount, this.duration);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      dropZoneParent.score += this.amount;
      return true;
    }

    return false;
  }

  public override ApplyEffect(): boolean {
    super.ApplyEffect();
    if (Client.state.lastScoredSection) {
      Client.state.lastScoredSection.score += this.amount;
      return true;
    }

    return false;
  }
}

export class AddSectionEffect extends Effect {
  constructor(
    readonly score: number = 10,
    readonly fieldSize: number = 30,
    readonly fieldColor: WheelSectionColors = WheelSectionColors.RED,
    readonly text: string | null = null,
    readonly effects: Effect[] = [],
    readonly pegs: number = 1,
  ) {
    super("effect_addSection.png", "Add Section", "Permanently add a section.", 0);
  }

  public Clone(): AddSectionEffect {
    return new AddSectionEffect(
      this.score,
      this.fieldSize,
      this.fieldColor,
      this.text,
      this.effects,
      this.pegs,
    );
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;
    if (!dropZoneParent) return false;

    const sectionIndex = dropZoneParent.wheel.sections.indexOf(dropZoneParent);
    if (sectionIndex === -1) return false;
    dropZoneParent.wheel.addSection(
      this.score,
      this.fieldSize,
      this.fieldColor,
      this.text,
      this.effects,
      this.pegs,
      sectionIndex,
    );
    return true;
  }
}

export class RemoveSectionEffect extends Effect {
  constructor() {
    super(
      "effect_demolition.png",
      "Remove Section",
      "Permanently remove a section.",
      0,
    );
  }

  public Clone(): RemoveSectionEffect {
    return new RemoveSectionEffect();
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;
    if (!dropZoneParent) return false;

    const sectionIndex = dropZoneParent.wheel.sections.indexOf(dropZoneParent);
    if (sectionIndex === -1) return false;
    dropZoneParent.wheel.removeSection(sectionIndex);
    return true;
  }
}

export class ModifyPegsEffect extends Effect {
  constructor(readonly amount: number) {
    super(
      `${amount < 0 ? "effect_removePeg.png" : "effect_addPeg.png"}`,
      `${amount < 0 ? "Remove" : "Add"} Pegs`,
      `Permanently ${amount < 0 ? "remove" : "add"} ${Math.abs(amount)} pegs to a section.`,
      0,
    );
  }

  public override Clone(): ModifyPegsEffect {
    return new ModifyPegsEffect(this.amount);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      let modified = false;

      if (this.amount < 0) {
        if (dropZoneParent.pegs <= 0) return false;

        for (let i = 0; i < Math.abs(this.amount); i++) {
          if (dropZoneParent.pegs <= 0) break;
          dropZoneParent.removePeg();
          modified = true;
        }
      } else {
        for (let i = 0; i < this.amount; i++) {
          dropZoneParent.addPeg();
          modified = true;
        }
      }

      if (modified) {
        // Trigger field redistribution animation
        dropZoneParent.wheel.drawSections();
        dropZoneParent.wheel.recomputePegs();
      }

      return modified;
    }
    return false;
  }
}

export class ModifyPegsAreaEffect extends Effect {
  constructor(readonly amount: number) {
    super(
      `${amount < 0 ? "effect_removePeg.png" : "effect_addPeg.png"}`,
      `${amount < 0 ? "Remove" : "Add"} Pegs (Area)`,
      `Permanently ${amount < 0 ? "remove" : "add"} ${Math.abs(amount)} pegs to a section and its neighbors.`,
      0,
    );
  }

  public override Clone(): ModifyPegsAreaEffect {
    return new ModifyPegsAreaEffect(this.amount);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      const wheel = dropZoneParent.wheel;
      const targetIndex = wheel.sections.indexOf(dropZoneParent);

      if (targetIndex === -1) return false;

      // Get the target section and its neighbors
      const sectionsToModify: WheelSection[] = [];
      const totalSections = wheel.sections.length;

      // Add target section
      sectionsToModify.push(dropZoneParent);

      // Add left neighbor (wrap around if needed)
      const leftIndex = (targetIndex - 1 + totalSections) % totalSections;
      sectionsToModify.push(wheel.sections[leftIndex]);

      // Add right neighbor (wrap around if needed)
      const rightIndex = (targetIndex + 1) % totalSections;
      sectionsToModify.push(wheel.sections[rightIndex]);

      // Apply the effect to all three sections
      let anyModified = false;
      for (const section of sectionsToModify) {
        if (this.amount < 0) {
          // Remove pegs (but don't go below 1)
          if (section.pegs > 1) {
            for (let i = 0; i < Math.abs(this.amount); i++) {
              if (section.pegs <= 1) break;
              section.removePeg();
            }
            anyModified = true;
          }
        } else {
          // Add pegs
          for (let i = 0; i < this.amount; i++) {
            section.addPeg();
          }
          anyModified = true;
        }
      }

      if (anyModified) {
        // Trigger field redistribution animation
        dropZoneParent.wheel.drawSections();
        dropZoneParent.wheel.recomputePegs();
      }

      return anyModified;
    }
    return false;
  }
}

export class ShrinkSectionEffect extends Effect {
  constructor(readonly shrinkPercent: number = 10) {
    super(
      "effect_boostScore.png",
      "Shrink Section",
      `Shrink a section by ${shrinkPercent}% and redistribute the size to its neighbors.`,
      0,
    );
  }

  public Clone(): ShrinkSectionEffect {
    return new ShrinkSectionEffect(this.shrinkPercent);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      const wheel = dropZoneParent.wheel;
      const targetIndex = wheel.sections.indexOf(dropZoneParent);

      if (targetIndex === -1) return false;

      const totalSections = wheel.sections.length;
      const shrinkAmount = dropZoneParent.fieldSize * (this.shrinkPercent / 100);
      const redistributeAmount = shrinkAmount / 2; // Split between two neighbors

      // Shrink the target section
      dropZoneParent.fieldSize -= shrinkAmount;

      // Get neighbors and redistribute
      const leftIndex = (targetIndex - 1 + totalSections) % totalSections;
      const rightIndex = (targetIndex + 1) % totalSections;

      wheel.sections[leftIndex].fieldSize += redistributeAmount;
      wheel.sections[rightIndex].fieldSize += redistributeAmount;

      // Update wheel visuals
      wheel.drawSections();
      wheel.recomputePegs();

      return true;
    }
    return false;
  }
}

export class LeechSectionSizeEffect extends Effect {
  constructor(readonly leechPercent: number = 5) {
    super(
      "effect_addSection.png",
      "Leech Section Size",
      `Steal ${leechPercent}% size from each neighbor and add it to this section.`,
      0,
    );
  }

  public Clone(): LeechSectionSizeEffect {
    return new LeechSectionSizeEffect(this.leechPercent);
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      const wheel = dropZoneParent.wheel;
      const targetIndex = wheel.sections.indexOf(dropZoneParent);

      if (targetIndex === -1) return false;

      const totalSections = wheel.sections.length;

      // Get neighbors
      const leftIndex = (targetIndex - 1 + totalSections) % totalSections;
      const rightIndex = (targetIndex + 1) % totalSections;

      const leftSection = wheel.sections[leftIndex];
      const rightSection = wheel.sections[rightIndex];

      // Calculate leech amounts
      const leftLeech = leftSection.fieldSize * (this.leechPercent / 100);
      const rightLeech = rightSection.fieldSize * (this.leechPercent / 100);

      // Leech from neighbors
      leftSection.fieldSize -= leftLeech;
      rightSection.fieldSize -= rightLeech;

      // Add to target section
      dropZoneParent.fieldSize += leftLeech + rightLeech;

      // Update wheel visuals
      wheel.drawSections();
      wheel.recomputePegs();

      return true;
    }
    return false;
  }
}

export class SliceSectionEffect extends Effect {
  constructor() {
    super(
      "effect_addSection.png",
      "Slice Section",
      "Split a section in half. Sharing effects and color, halving score and size.",
      0,
    );
  }

  public Clone(): SliceSectionEffect {
    return new SliceSectionEffect();
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      const wheel = dropZoneParent.wheel;
      const targetIndex = wheel.sections.indexOf(dropZoneParent);

      if (targetIndex === -1) return false;

      // Calculate split properties
      const originalScore = dropZoneParent.score;
      const originalSize = dropZoneParent.fieldSize;
      const originalColor = dropZoneParent.fieldColor;
      const originalText = dropZoneParent.text;
      const originalPegs = dropZoneParent.pegs;

      // Clone effects for the new section
      const clonedEffects = dropZoneParent.effects.map(effect => effect.Clone());

      // Split the original section
      const halfScore = Math.floor(originalScore / 2);
      const halfSize = originalSize / 2;
      const halfPegs = Math.max(1, Math.floor(originalPegs / 2)); // Minimum 1 peg

      // Modify the original section
      dropZoneParent.score = halfScore;
      dropZoneParent.fieldSize = halfSize;
      // Keep original pegs for now, will be adjusted when new section is added

      // Add the new section right after the original
      wheel.addSection(
        halfScore,           // Same score (rounded down)
        halfSize,           // Same size
        originalColor,      // Same color
        originalText,       // Same text
        clonedEffects,      // Cloned effects
        halfPegs,          // Half the pegs
        targetIndex + 1    // Insert right after original
      );

      // Adjust original section pegs
      const remainingPegs = Math.max(1, originalPegs - halfPegs);
      while (dropZoneParent.pegs > remainingPegs) {
        dropZoneParent.removePeg();
      }
      while (dropZoneParent.pegs < remainingPegs) {
        dropZoneParent.addPeg();
      }

      return true;
    }
    return false;
  }
}

export class CombineSameColorSectionsEffect extends Effect {
  constructor() {
    super(
      "effect_addSection.png",
      "Combine Sections",
      "Combine all neighboring sections of the same color into this section.",
      0,
    );
  }

  public Clone(): CombineSameColorSectionsEffect {
    return new CombineSameColorSectionsEffect();
  }

  public override OnUseEffect(dropZoneParent?: WheelSection): boolean {
    if (!super.OnUseEffect()) return false;

    if (dropZoneParent) {
      const wheel = dropZoneParent.wheel;
      const targetIndex = wheel.sections.indexOf(dropZoneParent);

      if (targetIndex === -1) return false;

      const targetColor = dropZoneParent.fieldColor;
      const totalSections = wheel.sections.length;
      const sectionsToRemove: number[] = [];

      // Find all consecutive neighbors with the same color
      let leftIndex = (targetIndex - 1 + totalSections) % totalSections;
      let rightIndex = (targetIndex + 1) % totalSections;

      // Check left neighbors
      while (leftIndex !== targetIndex && wheel.sections[leftIndex].fieldColor === targetColor) {
        sectionsToRemove.push(leftIndex);
        leftIndex = (leftIndex - 1 + totalSections) % totalSections;
      }

      // Check right neighbors
      while (rightIndex !== targetIndex && wheel.sections[rightIndex].fieldColor === targetColor) {
        sectionsToRemove.push(rightIndex);
        rightIndex = (rightIndex + 1) % totalSections;
      }

      // If no same-color neighbors found, do nothing
      if (sectionsToRemove.length === 0) {
        return false;
      }

      // Combine properties from all matching sections
      let totalScore = dropZoneParent.score;
      let totalSize = dropZoneParent.fieldSize;
      let totalPegs = dropZoneParent.pegs;
      const combinedEffects = [...dropZoneParent.effects];

      // Process each section to be removed
      sectionsToRemove.forEach(index => {
        const section = wheel.sections[index];
        totalScore += section.score;
        totalSize += section.fieldSize;
        totalPegs += section.pegs;

        // Add all effects from this section
        section.effects.forEach(effect => {
          combinedEffects.push(effect.Clone());
        });
      });

      // Apply combined properties to target section
      dropZoneParent.score = totalScore;
      dropZoneParent.fieldSize = totalSize;

      // Update pegs
      while (dropZoneParent.pegs < totalPegs) {
        dropZoneParent.addPeg();
      }

      // Add combined effects
      combinedEffects.forEach(effect => {
        if (!dropZoneParent.effects.includes(effect)) {
          dropZoneParent.effects.push(effect);
        }
      });

      // Remove the absorbed sections (sort indices in descending order to avoid index shifting)
      sectionsToRemove.sort((a, b) => b - a);
      sectionsToRemove.forEach(index => {
        wheel.removeSection(index);
      });

      return true;
    }
    return false;
  }
}

export class MultiplyScoreEffect extends Effect {
  constructor(
    readonly multiplier: number,
    duration: number = 1, // Default to single use
  ) {
    const isMultiUse = duration > 1;
    super(
      "effect_boostScore.png",
      "Multiply Score",
      isMultiUse
        ? `Multiply win by x${multiplier} for ${duration} rounds!`
        : `Multiply win by x${multiplier}!`,
      duration,
    );
  }

  public Clone(): MultiplyScoreEffect {
    return new MultiplyScoreEffect(this.multiplier, this.duration);
  }

  public override ApplyEffect(): boolean {
    super.ApplyEffect();
    Client.state.win = Math.floor(Client.state.win * this.multiplier);
    return true;
  }
}

export class ModifyAllSectionScoresEffect extends Effect {
  constructor(
    readonly amount: number,
    duration: number = 1, // Default to single use
  ) {
    const isMultiUse = duration > 1;
    super(
      "effect_modify_all_sections.png",
      `${amount < 0 ? "Remove" : "Add"} Score (All Sections)`,
      isMultiUse
        ? `${amount < 0 ? "Remove" : "Add"} ${Math.abs(amount)} points to ALL sections for ${duration} rounds!`
        : `${amount < 0 ? "Remove" : "Add"} ${Math.abs(amount)} points to ALL sections!`,
      duration,
    );
  }

  public Clone(): ModifyAllSectionScoresEffect {
    return new ModifyAllSectionScoresEffect(this.amount, this.duration);
  }

  public override ApplyEffect(): boolean {
    super.ApplyEffect();

    // Get the wheel from the active scene
    const activeScene = Client.getActiveScene();
    if (!activeScene || !activeScene.wheel) {
      return false;
    }

    const wheel = activeScene.wheel;
    let modified = false;

    // Apply the score modification to ALL sections
    wheel.sections.forEach(section => {
      section.score += this.amount;
      modified = true;
    });

    return modified;
  }
}

export class ModifyScoreEffect extends Effect {
  constructor(
    readonly amount: number,
    duration: number = 1, // Default to single use for backward compatibility
  ) {
    const isMultiUse = duration > 1;
    super(
      "effect_boostScore.png",
      `${amount < 0 ? "Lose" : "Gain"} Score`,
      isMultiUse
        ? `${amount < 0 ? "Lose" : "Gain"} ${Math.abs(amount)} points for ${duration} uses!`
        : `${amount < 0 ? "Lose" : "Gain"} ${Math.abs(amount)} points!`,
      duration,
    );
  }

  public Clone(): ModifyScoreEffect {
    return new ModifyScoreEffect(this.amount, this.duration);
  }

  public override ApplyEffect(): boolean {
    super.ApplyEffect(); // This decrements duration and handles expiration
    Client.state.win += this.amount; // Adds to current win score
    return true;
  }
}


